import React, {useEffect, useMemo, useState} from "react"
import {useLocation, useNavigate} from "react-router-dom"
import CalendarForm from "@/components/calendar/CalendarForm"
import Summary from "@/components/calendar/Summary"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import EditRoundedIcon from "@mui/icons-material/EditRounded"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import FavoriteIcon from "@mui/icons-material/Favorite"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import CondominioInfo from "@/components/condos/CondoInfo"
import {YouTubeEmbed, TikTokEmbed} from "react-social-media-embed"
import ImagesColumn from "@/components/carrousel/ImagesColumn"
import ShareBtn from "@/components/properties/ShareBtn"
import OpportunityTagBadge from "@/components/properties/OpportunityTagBadge"
import {inmAndCondIndStyles as styles} from "@/constants"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Button from "@mui/material/Button"
import Paper from "@mui/material/Paper"
import TextField from "@mui/material/TextField"
import ShareIcon from "@mui/icons-material/Share"
import copy from "copy-to-clipboard"
import dayjs from "dayjs"
import {downloadPropertyReport, getInmuebleById} from "@/api/properties"
import {deleteDate as deleteDateApi} from "@/api/properties"
import Page from "@/layouts/Page"
import {clearCache, displayZoneLabel, getImageURL, getPriceAnalysisStartDate} from "@/utils"
import EditButton from "@/components/properties/EditButton"
import {PROPERTY_CHARACTERISTICS, PROPERTY_SECTIONS} from "@/components/properties/ConstantData"
import PageSection from "@/layouts/PageSection"
import {useAuthContext} from "@/hooks/useAuthContext"
import {useShowMoreList} from "@/hooks/useShowMoreList"
import StatusIndicator from "@/components/properties/StatusIndicator"
import StatusTile from "@/components/properties/StatusTile"
import {regionTextByCode} from "@casas-diamante/types"
import FileSection from "@/components/files/FilesSection"
import RecorridosSection from "@/components/properties/RecorridosSection"
import PropertyReviewsSection from "@/components/properties/PropertyReviewsSection"
import {normalizeSeaviewValue} from "@/types/Seaview"
import {getShareUrl} from "@/utils"
import {PropertySearchState, readPersistedPropertySearchState} from "@/hooks/useBackNavigation"
import PropertyViewCount from "@/components/properties/PropertyViewCount"
import PropertyStats from "@/components/properties/PropertyStats"
import {useResolvedPropertyZone} from "@/hooks/useResolvedPropertyZone"
import {useOwnerNotes} from "@/hooks/useOwnerNotes"
import PropertyPriceComparisonTable from "@/components/properties/PropertyPriceComparisonTable"
import PropertyIndicatorsTable from "@/components/properties/PropertyIndicatorsTable"
import PropertyPriceSummaryTable from "@/components/properties/PropertyPriceSummaryTable"
import {PropertyPriceSummary, SerializedFirestoreTimestamp} from "@/types/Property"
import type {PropertyIndicatorsProperty} from "@/components/properties/PropertyIndicatorsTable"
import {
   useGetPropertyPriceAnalysisQuery,
   type PropertyPriceAnalysisResponse,
} from "@/api/properties"

function PropertyAnalysisTables({
   property,
   scheme,
   priceAnalysis,
   loadingPriceAnalysis,
   priceAnalysisError,
}: {
   property: PropertyIndicatorsProperty &
      PropertyPriceSummary & {createdAt?: SerializedFirestoreTimestamp | null}
   scheme: string
   priceAnalysis?: PropertyPriceAnalysisResponse
   loadingPriceAnalysis: boolean
   priceAnalysisError: boolean
}) {
   return (
      <section className="mb-8 rounded border border-gray-200 bg-white p-2 shadow-sm sm:p-4">
         <h2 className="mb-4 font-bold text-blue">Análisis de Precios e Indicadores</h2>
         <h3 className="mb-1 font-bold text-blue">Datos generales del inmueble</h3>
         <PropertyPriceSummaryTable property={property} />
         <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <PropertyPriceComparisonTable
               rows={priceAnalysis?.rows ?? []}
               loading={loadingPriceAnalysis}
               error={priceAnalysisError}
            />
            <PropertyIndicatorsTable property={property} scheme={scheme} />
         </div>
         <p className="mt-4 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-gray-500">
            Los datos corresponden a la actividad registrada en Casas Diamante desde{" "}
            {getPriceAnalysisStartDate(property.createdAt)}.
         </p>
      </section>
   )
}

function PropertyPage() {
   // get the values sent from the useNavigate()
   const location = useLocation()
   const navigate = useNavigate()
   // get the id of the inmueble
   // se divide el url por sus slashes y se agarra la segunda posicion, que es el id
   const id = location.pathname.split("/")[2]
   const {
      data: priceAnalysis,
      isLoading: loadingPriceAnalysis,
      isError: priceAnalysisError,
   } = useGetPropertyPriceAnalysisQuery(id)
   // state
   const [property, setProperty] = useState<any>()
   const [scheme, setScheme] = useState<string>("")
   // const show calendar form
   const [showEditCalendarForm, setShowEditCalendarForm] = useState(false)
   const [disableDelete, setDisableDelete] = useState(false)
   const [currDate, setCurrDate] = useState<any>(null)
   const [currNote, setCurrNote] = useState<any>(null)
   const [date, setDate] = useState<any>(null)
   const [displayCopied, setDisplayCopied] = useState(false)
   const [generatingReport, setGeneratingReport] = useState(false)
   const [reportError, setReportError] = useState("")
   const {user} = useAuthContext()
   const resolvedZone = useResolvedPropertyZone(property)
   const {
      editingOwnerNotes,
      ownerNotesDraft,
      setOwnerNotesDraft,
      savingOwnerNotes,
      ownerNotesError,
      startEditingOwnerNotes,
      cancelEditingOwnerNotes,
      saveOwnerNotes,
   } = useOwnerNotes({
      propertyId: id,
      ownerNotes: property?.ownerNotes,
      onSaved: ownerNotes => {
         if (property) {
            setProperty({...property, ownerNotes: ownerNotes ?? undefined})
         }
      },
   })

   const calendarReservations = useMemo(() => {
      const calendar = property?.calendar ?? []
      const getStartSeconds = (entry: any) => {
         const start = entry?.start
         if (!start) return 0
         if (typeof start._seconds === "number") return start._seconds
         if (typeof start.seconds === "number") return start.seconds
         if (start instanceof Date) return Math.floor(start.getTime() / 1000)
         if (typeof start === "number") return start
         return 0
      }
      return [...calendar].sort((a, b) => getStartSeconds(b) - getStartSeconds(a))
   }, [property?.calendar])
   const {
      visibleItems: displayedReservations,
      hasMore: hasMoreReservations,
      remainingCount: remainingReservations,
      totalCount: totalReservations,
      loadMore: handleLoadMoreReservations,
   } = useShowMoreList(calendarReservations, {initialCount: 5, step: 5})

   // hide calendar form
   const hideEditCalendarForm = () => {
      setShowEditCalendarForm(false)
   }

   useEffect(() => {
      let isMounted = true
      const state: any = location.state

      const getInm = async () => {
         try {
            if (state?.property && isMounted) {
               setProperty(state.property)
            }
            const res = await getInmuebleById(id)
            if (isMounted) {
               setProperty(res)
            }
         } catch (error) {
            console.log("Error de inmueble: " + error)
         }
      }
      getInm()
      return () => {
         isMounted = false
      }
   }, [id, location.state])

   useEffect(() => {
      if (location.pathname.includes("renta")) {
         setScheme("rent")
      } else if (location.pathname.includes("venta")) {
         setScheme("sale")
      } else {
         setScheme("")
      }
      window.scrollTo(0, 0)
   }, [location.pathname])

   // Invalidate property cache when a new date is added, and fetch the new data
   const invalidateCache = async () => {
      try {
         clearCache()
         const res = await getInmuebleById(id)
         setProperty(res)
         setDisableDelete(false)
         window.history.replaceState({}, "")
      } catch (error) {
         console.log("Error de inmueble: " + error)
      }
   }

   const handleEdit = (date: any) => {
      // timestamp to date
      const start = new Date((date.start._seconds + date.start._nanoseconds * 10 ** -9) * 1000)
      const end = new Date((date.end._seconds + date.end._nanoseconds * 10 ** -9) * 1000)
      setDate(date)
      setCurrDate([start, end])
      setCurrNote(date.notes)
      setShowEditCalendarForm(true)
   }

   const handleBack = () => {
      const state = location.state as {property?: any; fromSearch?: PropertySearchState} | null
      const persistedSearch = readPersistedPropertySearchState()

      if (state?.fromSearch) {
         if (state.fromSearch.source === "properties") {
            const restoredSearch =
               persistedSearch?.source === "properties" ? persistedSearch : state.fromSearch

            navigate(restoredSearch.returnTo, {
               state: {
                  restoredSearch,
               },
            })
            return
         }

         navigate(state.fromSearch.returnTo)
         return
      }

      navigate(-1)
   }

   const deleteDate = async (dateToDelete: any) => {
      setDisableDelete(true)
      try {
         await deleteDateApi(id, dateToDelete, property.calendar)
         invalidateCache()
      } catch (error) {
         console.log("Error al borrar fecha: " + error)
         setDisableDelete(false)
      }
   }

   const copyToClipboard = async () => {
      setDisplayCopied(true)
      copy(
         `${getShareUrl(
            property.region,
            property.type,
            scheme === "rent" ? "rent" : "sale",
            property.slug,
         )}`,
         {
            debug: true,
            message: "Hello",
         },
      )
      setTimeout(() => {
         setDisplayCopied(false)
      }, 2000)
   }

   const generateReport = async () => {
      setGeneratingReport(true)
      setReportError("")
      try {
         const response = await downloadPropertyReport(
            id,
            scheme === "sale" || scheme === "rent" ? scheme : undefined,
         )
         const url = window.URL.createObjectURL(response.data)
         const anchor = document.createElement("a")
         anchor.href = url
         const contentDisposition = response.headers["content-disposition"] as string | undefined
         const encodedFilename = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
         let filename: string | undefined
         if (encodedFilename) {
            try {
               filename = decodeURIComponent(encodedFilename)
            } catch {
               filename = undefined
            }
         }
         if (!filename) {
            const dateParts = new Intl.DateTimeFormat("en", {
               timeZone: "America/Mexico_City",
               year: "numeric",
               month: "2-digit",
               day: "2-digit",
            }).formatToParts(new Date())
            const date = [
               dateParts.find(part => part.type === "year")?.value,
               dateParts.find(part => part.type === "month")?.value,
               dateParts.find(part => part.type === "day")?.value,
            ].join("_")
            const safeName = Array.from(
               (property?.name ?? id).replace(/[<>:"/\\|?*]/g, "-"),
               (character: string) => {
                  const codePoint = character.codePointAt(0) ?? 0
                  return codePoint <= 31 || (codePoint >= 127 && codePoint <= 159)
                     ? "-"
                     : character
               },
            )
               .join("")
               .replace(/\s+/g, " ")
               .trim()
               .replace(/[. ]+$/g, "")
            filename = `${safeName || id} - ${date}.pdf`
         }
         anchor.download = filename
         document.body.appendChild(anchor)
         anchor.click()
         anchor.remove()
         window.URL.revokeObjectURL(url)
      } catch (error) {
         console.error("Error generando reporte", error)
         setReportError("No se pudo generar el reporte. Intenta de nuevo.")
      } finally {
         setGeneratingReport(false)
      }
   }

   const SchemeBtn = ({sale = true}: {sale?: boolean}) => {
      const selected = (sale && scheme === "sale") || (!sale && scheme === "rent")
      const targetPath = sale ? "venta" : "renta"
      const targetScheme = sale ? "sale" : "rent"
      return (
         <button
            className={`border-2 border-blue ${
               selected ? "text-white bg-blue" : "text-blue"
            } rounded px-8`}
            onClick={
               selected
                  ? () => {
                       setScheme("")
                       navigate(`/inmuebles/${id}`)
                    }
                  : () => {
                       setScheme(targetScheme)
                       navigate(`/inmuebles/${id}/${targetPath}`)
                    }
            }
         >
            {sale ? "Venta" : "Renta"}
         </button>
      )
   }

   const EnvironmentP = ({env, key}: {env: string; key: any}) => (
      <p key={key} className="px-2 py-1 border border-blue text-blue rounded">
         {env}
      </p>
   )

   const seaviewStatus = normalizeSeaviewValue(property?.seaview)
   const formatMoney = (value: number) =>
      value.toLocaleString("en-US", {
         minimumFractionDigits: 0,
         maximumFractionDigits: 0,
      })

   if (property) {
      return (
         <Page className="md:p-8 max-w-[1600px] mx-auto">
            <ShareBtn
               region={property.region}
               name={property.name}
               zone={resolvedZone || ""}
               capacity={property.capacity}
               surface={property.mts}
               constructionMts={property.mtsConstruction}
               condoId={(property.condo && property.condo) || ""}
               explicitRent={scheme === "rent"}
               costs={{
                  ...property.costos,
                  maintenance: property.maintenance,
                  salePrice: property.costoVenta,
                  pricePerMeter: property.pricePerMeter,
               }}
               slug={property.slug}
               description={property.desc2}
               type={property.type}
               videos={property.videos}
               mapsShareUrl={property.mapsShareUrl}
               accessToBeach={property.accessToBeach}
            />
            <section className="flex flex-col md:flex-row md:items-center justify-between">
               <ArrowBackIcon
                  className="font-bold mb-7 cursor-pointer mt-5 flex"
                  onClick={handleBack}
               />
               {(user?.admin || user?.worker) && (
                  <div className="flex gap-4 items-center justify-center flex-col sm:flex-row">
                     <EditButton
                        label={generatingReport ? "Generando reporte..." : "Generar reporte PDF"}
                        onClick={generateReport}
                        icon={<PictureAsPdfIcon fontSize="small" />}
                        disabled={generatingReport}
                     />
                     {user.admin && (
                        <>
                           <EditButton
                              label="Editar inmueble"
                              onClick={() =>
                                 navigate(`/inmuebles/editInmueble/${id}`, {
                                    state: {inmueble: property},
                                 })
                              }
                              icon={<EditRoundedIcon fontSize="small" />}
                           />
                           <EditButton
                              label="Editar fotos"
                              onClick={() =>
                                 navigate(`/inmuebles/editPhotos/${id}`, {
                                    state: {inmueble: property},
                                 })
                              }
                              icon={<CameraAltIcon fontSize="small" />}
                           />
                           <EditButton
                              label="Editar archivos"
                              onClick={() =>
                                 navigate(`/inmuebles/editFiles/${id}`, {
                                    state: {inmueble: property},
                                 })
                              }
                              icon={<InsertDriveFileIcon fontSize="small" />}
                           />
                        </>
                     )}
                  </div>
               )}
            </section>
            {reportError && <p className="mb-4 text-center text-sm text-red-600">{reportError}</p>}
            {property.createdAt && (
               <p className="my-5 text-center">
                  Creado el: {dayjs.unix(property.createdAt?._seconds).format("DD-MM-YYYY")}
               </p>
            )}
            <section className="flex gap-4 items-center justify-center flex-wrap">
               <StatusIndicator
                  label={
                     property.suspended
                        ? "Apagado"
                        : property.suspended === undefined
                        ? "Sin actualizar"
                        : "Encendido"
                  }
                  condition={!property.suspended}
               />
               <StatusIndicator
                  label={
                     property.available
                        ? "Disponible"
                        : property.available === undefined
                        ? "Sin actualizar"
                        : "No disponible"
                  }
                  condition={property.available}
               />
               <StatusTile
                  status={property.status}
                  className="border border-gray-300 px-4 py-1 rounded-md"
               />
            </section>
            <section className="my-4 flex items-center justify-center gap-4">
               {property.forSale && <SchemeBtn />}
               {property.forRent && <SchemeBtn sale={false} />}
            </section>
            <section className="grid grid-cols-2 md:flex md:flex-wrap gap-4 items-center justify-center">
               {PROPERTY_SECTIONS.map((section, i) => {
                  if (section.id === "property-calendar" && !property.calendar) return null
                  if (
                     section.id === "property-reviews" &&
                     !(property.forRent && scheme !== "sale")
                  ) {
                     return null
                  }
                  return (
                     <button
                        key={i}
                        className="bg-blue rounded text-white px-4 py-1"
                        onClick={() => {
                           const element = document.getElementById(section.id)
                           element?.scrollIntoView({behavior: "smooth"})
                        }}
                     >
                        {section.title}
                     </button>
                  )
               })}
            </section>
            <header className="flex flex-col md:flex-row items-center justify-center mb-8 md:min-h-[400px]">
               <img
                  src={getImageURL(property.mainImage)}
                  alt="mainImage"
                  className="md:w-[50%] h-auto w-full object-cover rounded-lg mx-auto my-5"
               />
               <div className="md:w-[50%] w-full md:ml-4">
                  <div className="flex items-center">
                     <h1>{property.name}</h1>
                     <button
                        onClick={() => {
                           copyToClipboard()
                        }}
                        disabled={displayCopied}
                     >
                        <ContentCopyIcon className="ml-4" />
                        <em className="font-semibold text-sm ml-1">
                           {displayCopied ? "¡Dirección copiada!" : "Copiar para clientes"}
                        </em>
                     </button>
                  </div>
                  <h2 className="text-green">
                     {regionTextByCode[property.region as keyof typeof regionTextByCode]}
                  </h2>
                  {property.condoName && (
                     <h3
                        className="cursor-pointer underline"
                        onClick={() => {
                           const element = document.getElementById("condo-info")
                           element?.scrollIntoView({behavior: "smooth"})
                        }}
                     >
                        {property.condoName}
                        {property.condoClass && <span> - {property.condoClass}</span>}
                     </h3>
                  )}
                  {resolvedZone && <p>{displayZoneLabel(resolvedZone)}</p>}
                  <p>{property.type}</p>
                  <p>{property.accessToBeach}</p>
                  <section className="flex flex-col mb-4">
                     <PropertyStats
                        stats={[
                           {
                              count: property.likes,
                              label: "likes",
                              icon: <FavoriteIcon fontSize="small" className="text-red-500" />,
                              valueClassName: "text-md font-semibold text-gray-800",
                           },
                           {
                              count: property.shares,
                              label: "shares",
                              icon: <ShareIcon fontSize="small" />,
                           },
                        ]}
                     />
                     <OpportunityTagBadge
                        tag={property.opportunityTag}
                        className="self-start ml-1 mt-2"
                     />
                  </section>
                  <PropertyViewCount
                     region={property.region}
                     type={property.type}
                     scheme={
                        scheme === "rent"
                           ? "rent"
                           : scheme === "sale"
                           ? "sale"
                           : property.forRent
                           ? "rent"
                           : "sale"
                     }
                     slug={property.slug}
                  />

                  <div className="flex flex-wrap gap-4 my-4">
                     {PROPERTY_CHARACTERISTICS.map(
                        (c, i) =>
                           property[c.key] && (
                              <div key={i} className="flex items-center gap-2">
                                 {c.icon}
                                 {property[c.key]}
                                 &nbsp;
                                 {c.suffix}
                              </div>
                           ),
                     )}
                  </div>
                  {property.environments && (
                     <>
                        <h3>Incluye</h3>
                        <div className="flex flex-wrap gap-2">
                           {property.environments?.map((a: string, i: number) => (
                              <EnvironmentP env={a} key={i} />
                           ))}
                        </div>
                     </>
                  )}
                  <h3 className="mt-4">Ver ubicación</h3>
                  <p className="truncate">
                     <a
                        href={property.mapsShareUrl}
                        target="_blank"
                        className="text-blue underline"
                        rel="noreferrer"
                     >
                        {property.mapsShareUrl}
                     </a>
                  </p>
                  <p className="mb-4">
                     Dirección:{" "}
                     {property.address
                        ? property.address
                        : property.condoAddress
                        ? property.condoAddress
                        : "Sin dirección"}
                  </p>
                  {user.admin && property.saleComission && (
                     <p className="text-green">
                        Comisión de venta pactada: {property.saleComission}%
                     </p>
                  )}
                  {user.admin ? (
                     property.partnerNames ? (
                        <p className="text-green">{`Responsable: ${property.partnerNames.map(
                           (name: string) => name,
                        )}`}</p>
                     ) : (
                        "Sin responsable"
                     )
                  ) : null}
               </div>
            </header>
            <PropertyAnalysisTables
               property={property}
               scheme={scheme}
               priceAnalysis={priceAnalysis}
               loadingPriceAnalysis={loadingPriceAnalysis}
               priceAnalysisError={priceAnalysisError}
            />
            <PageSection id="property-info" title="Información" className="flex flex-col gap-4">
               <h2 className="text-blue">Precios</h2>
               <section className="grid grid-cols-1 md:grid-cols-2">
                  <div>
                     {scheme !== "rent" && (
                        <>
                           <h3 className="text-green">Venta</h3>
                           {property.costoVenta && (
                              <p>Precio de Venta: {`$${formatMoney(property.costoVenta)} MXN`}</p>
                           )}
                           {property.maintenance && (
                              <p>Mantenimiento: {`$${formatMoney(property.maintenance)} MXN`}</p>
                           )}
                           {property.pricePerMeter && (
                              <p>
                                 Precio por metro cuadrado:{" "}
                                 {`$${formatMoney(property.pricePerMeter)}`}
                              </p>
                           )}
                        </>
                     )}
                  </div>
                  <div>
                     {scheme !== "sale" && (
                        <>
                           <h3 className="text-green">Renta</h3>
                           {property.costos?.noche_regular && (
                              <p>
                                 Precio noche regular:{" "}
                                 {`$${formatMoney(property.costos.noche_regular)} MXN`}
                              </p>
                           )}
                           {property.costos?.noche_media && (
                              <p>
                                 Precio noche media:{" "}
                                 {`$${formatMoney(property.costos.noche_media)} MXN`}
                              </p>
                           )}
                           {property.costos?.noche_alta && (
                              <p>
                                 Precio noche alta:{" "}
                                 {`$${formatMoney(property.costos.noche_alta)} MXN`}
                              </p>
                           )}
                           {property.costos?.mensual_regular && (
                              <p>
                                 Precio mensual regular:{" "}
                                 {`$${formatMoney(property.costos.mensual_regular)} MXN`}
                              </p>
                           )}
                           {property.costos?.mensual_anual && (
                              <p>
                                 Precio mensual anual:{" "}
                                 {`$${formatMoney(property.costos.mensual_anual)} MXN`}
                              </p>
                           )}
                        </>
                     )}
                  </div>
               </section>
               {property.desc2 && (
                  <div>
                     <h3 className="text-blue">Descripción Breve</h3>
                     <p className="whitespace-pre-wrap">{property.desc2}</p>
                  </div>
               )}
               {property.desc && (
                  <div>
                     <h3 className="text-blue">Descripción</h3>
                     <p className="text-md whitespace-pre-wrap">{property.desc}</p>
                  </div>
               )}
               <div>
                  <h3 className="text-blue">Información adicional</h3>
                  <ul className="list-disc list-inside">
                     {property.furnished ? (
                        <li>El inmueble esta amueblado.</li>
                     ) : (
                        <li>El inmueble no se encuentra amueblado.</li>
                     )}
                     {property.house_antiquity ? <li>{property.house_antiquity}.</li> : <></>}
                     {property.outing_to_the_beach ? (
                        <li>El inmueble tiene salida a la playa.</li>
                     ) : (
                        <li>El inmueble no tiene salida a la playa.</li>
                     )}
                     {seaviewStatus === "yes" && <li>El inmueble tiene vista al mar.</li>}
                     {seaviewStatus === "no" && <li>El inmueble no tiene vista al mar.</li>}
                     {seaviewStatus === "partial" && (
                        <li>El inmueble tiene vista parcial al mar.</li>
                     )}
                     {seaviewStatus === null && <li>No hay información sobre la vista al mar.</li>}
                  </ul>
               </div>
               {property.furnished && property.furnishType && (
                  <p className="mb-4">{property.furnishType}</p>
               )}
            </PageSection>
            <PageSection
               id="property-notes"
               title="Notas"
               className="flex flex-col gap-4 items-center"
            >
               <div className="rounded-lg border-2 border-gray-300 w-full md:max-w-[50vw] p-4 whitespace-pre-wrap">
                  <h4 className="text-blue text-center">Notas públicas</h4>
                  <p className="whitespace-pre-wrap truncate">
                     {property.notes ? property.notes : "Sin notas públicas"}
                  </p>
               </div>
               <div className="rounded-lg border-2 border-green w-full md:max-w-[50vw] p-4 whitespace-pre-wrap">
                  <h4 className="text-green text-center">Notas privadas</h4>
                  <p className="text-gray-400 text-sm text-center">
                     Las notas privadas solo podrán verse en el sitio de Casas Diamante para Socios
                  </p>
                  <p className="whitespace-pre-wrap truncate">
                     {property.privateNotes ? property.privateNotes : "Sin notas privadas"}
                  </p>
               </div>
               <div className="rounded-lg border-2 border-blue w-full md:max-w-[50vw] p-4 whitespace-pre-wrap">
                  <div className="mb-3 flex items-center justify-between gap-3">
                     <h4 className="text-blue text-center">Notas al propietario</h4>
                     {user?.admin && !editingOwnerNotes && (
                        <Button
                           size="small"
                           variant="outlined"
                           startIcon={<EditIcon fontSize="small" />}
                           onClick={startEditingOwnerNotes}
                        >
                           Editar
                        </Button>
                     )}
                  </div>
                  {editingOwnerNotes ? (
                     <div className="flex flex-col gap-3">
                        <TextField
                           label="Notas al propietario"
                           multiline
                           minRows={4}
                           fullWidth
                           value={ownerNotesDraft}
                           onChange={event => setOwnerNotesDraft(event.target.value)}
                           disabled={savingOwnerNotes}
                        />
                        {ownerNotesError && <p className="text-sm text-red-600">{ownerNotesError}</p>}
                        <div className="flex justify-end gap-3">
                           <Button onClick={cancelEditingOwnerNotes} disabled={savingOwnerNotes}>
                              Cancelar
                           </Button>
                           <Button
                              variant="contained"
                              onClick={saveOwnerNotes}
                              disabled={savingOwnerNotes}
                           >
                              {savingOwnerNotes ? "Guardando..." : "Guardar"}
                           </Button>
                        </div>
                     </div>
                  ) : (
                     <p className="whitespace-pre-wrap truncate">
                        {property.ownerNotes ? property.ownerNotes : "Sin notas al propietario"}
                     </p>
                  )}
               </div>
            </PageSection>
            <PageSection id="property-files" title="Archivos">
               <FileSection id={property.id} />
            </PageSection>
            <RecorridosSection propertyId={id} canManageRecorridos={user?.admin ?? false} />
            {property.forRent && scheme !== "sale" && (
               <PropertyReviewsSection propertyId={id} canManageReviews={user?.admin ?? false} />
            )}
            {property.forRent && scheme !== "sale" && (
               <PageSection id="property-calendar" title="Calendario">
                  <div className="text-center my-2">
                     <CalendarForm
                        inmuebleId={id}
                        fetchInmueble={invalidateCache}
                        calendarPast={property.calendar}
                        currDate={showEditCalendarForm ? currDate : null} // Si es edición, pasamos la fecha actual
                        currNote={showEditCalendarForm ? currNote : ""} // Pasamos las notas si es edición
                        apiDate={showEditCalendarForm ? date : null} // Pasamos apiDate si es edición
                        hideForm={showEditCalendarForm ? hideEditCalendarForm : undefined}
                     />
                  </div>
                  <TableContainer component={Paper}>
                     <Table sx={{minWidth: 700}} aria-label="simple table">
                        <TableHead>
                           <TableRow>
                              <TableCell className="justify-center" align="center">
                                 Ocupación Propietario / CD
                              </TableCell>
                              <TableCell className="justify-center" align="center">
                                 Notas
                              </TableCell>
                              <TableCell align="center">Desde</TableCell>
                              <TableCell align="center">Hasta</TableCell>
                              <TableCell align="center">Editar</TableCell>
                              <TableCell align="center">Eliminar</TableCell>
                           </TableRow>
                        </TableHead>
                        <TableBody sx={{width: "100%"}}>
                           {displayedReservations.map((date: any, index: number) => (
                              <TableRow
                                 key={index}
                                 sx={{
                                    "&:last-child td, &:last-child th": {
                                       border: 0,
                                    },
                                 }}
                              >
                                 <TableCell
                                    align="center"
                                    sx={{
                                       maxWidth: "120px",
                                       overflowWrap: "break-word",
                                    }}
                                 >
                                    {date.rentedBy}
                                 </TableCell>
                                 <TableCell
                                    sx={{
                                       maxWidth: "120px",
                                       overflowWrap: "break-word",
                                    }}
                                 >
                                    {date.notes}
                                 </TableCell>
                                 <TableCell align="center">
                                    {dayjs.unix(date.start._seconds).format("DD-MM-YYYY")}
                                 </TableCell>
                                 <TableCell align="center">
                                    {dayjs.unix(date.end._seconds).format("DD-MM-YYYY")}
                                 </TableCell>
                                 <TableCell align="center">
                                    {!showEditCalendarForm && (
                                       <EditIcon
                                          className="text-green-600 self-center cursor-pointer"
                                          onClick={() => handleEdit(date)}
                                       />
                                    )}
                                    {showEditCalendarForm && (
                                       <EditIcon className="text-gray-400 self-center cursor-pointer" />
                                    )}
                                 </TableCell>
                                 <TableCell align="center">
                                    {!disableDelete && (
                                       <DeleteIcon
                                          className="text-red-600 self-center cursor-pointer"
                                          onClick={() => deleteDate(date)}
                                       />
                                    )}
                                    {disableDelete && (
                                       <DeleteIcon className="text-gray-400 self-center cursor-pointer" />
                                    )}
                                 </TableCell>
                              </TableRow>
                           ))}
                        </TableBody>
                     </Table>
                  </TableContainer>
                  {hasMoreReservations && (
                     <div className="flex justify-center my-4">
                        <Button variant="outlined" onClick={handleLoadMoreReservations}>
                           {`Mostrar ${Math.min(
                              remainingReservations,
                              5,
                           )} reservaciones más de ${totalReservations}`}
                        </Button>
                     </div>
                  )}
                  <Summary property={property} />
               </PageSection>
            )}
            <PageSection id="property-videos" title="Videos" className="flex flex-col items-center">
               {property.videos?.map((url: string, index: number) => {
                  if (url.includes("youtube")) {
                     return (
                        <div key={index}>
                           <div className="md:hidden">
                              <YouTubeEmbed url={url} width={300} height={200} />
                           </div>
                           <div className="md:flex hidden">
                              <YouTubeEmbed url={url} width={700} height={400} />
                           </div>
                        </div>
                     )
                  } else if (url.includes("tiktok")) {
                     return (
                        <div key={index} className="flex">
                           <TikTokEmbed url={url} />{" "}
                        </div>
                     )
                  } else {
                     return null
                  }
               })}
            </PageSection>
            <PageSection id="property-location" title="Ubicación">
               <section className="flex flex-col items-center text-center mx-4 md:mx-[20%] max-w-full">
                  {property.mapsUrl && (
                     <iframe
                        src={property.mapsUrl}
                        className="my-6 h-[200px] w-[300px] md:h-[450px] md:w-[700px] mx-auto"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                     />
                  )}
                  {property.mapsShareUrl && (
                     <>
                        <p className="font-bold">Ver Ubicación</p>
                        <a
                           href={property.mapsShareUrl}
                           target="_blank"
                           rel="noreferrer"
                           className="text-blue underline truncate"
                        >
                           {property.mapsShareUrl}
                        </a>
                     </>
                  )}
                  {property.address && (
                     <p className="my-4 text-sm md:mx-[20%]">Dirección: {property.address}</p>
                  )}
               </section>
            </PageSection>
            <PageSection id="property-images" title="Imágenes">
               {property.images && property.orderedSections && (
                  <ImagesColumn
                     images={property.images}
                     orderedSections={property.orderedSections}
                  />
               )}
            </PageSection>
            <hr className="font-bold text-2xl mb-10"></hr>
            {property.condo && (
               <CondominioInfo condId={property.condo} styles={styles} shouldNavigate lazy />
            )}
         </Page>
      )
   }

   return (
      <Page>
         <></>
      </Page>
   )
}

export default PropertyPage
